import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { AvailabilityMap, ItemListOption } from '@/contexts/item-context-types';
import { ChooseSubstitutionList } from './choose-substitution-list';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { ItemDetailContextProvider } from '@/contexts/item-detail-context';
import { ItemWithSubstitute } from '@/components/orders/substitution-item-wrapper';
import { SearchSubstitutionsContent } from './search-substitutions-content';
import {
  Props as SelectQuantityProps,
  SelectSubstitutionQuantityContent,
} from './select-substitution-quantity-content';
import { StyleSheet, useWindowDimensions } from 'react-native';
import { SubstitutionChoiceParams } from './types';
import { useSubstitutionCatalogSearchEnabled } from '@/hooks/use-dynamic-config-value';

type QuantityPropsStateType = Omit<
  SelectQuantityProps,
  'itemWithSubstitute' | 'orderUUID' | 'onSetSubstitution'
>;

export interface Props {
  availabilityMap: AvailabilityMap;
  fulfillmentDate: string;
  itemWithSubstitute: ItemWithSubstitute<ItemListOption>;
  onClose: (payload?: SubstitutionChoiceParams) => void;
  orderUUID: string;
  substitutionListOptions: ItemListOption[];
  variantPacksInOrder: Set<string>;
}

enum ChooseSubstitutionRoutes {
  GetOptions = 'GET_OPTIONS',
  SearchCatalog = 'SEARCH_CATALOG',
  SelectSubstitutionQuantity = 'SELECT_SUBSTITUTION_QUANTITY',
}

export const SubstitutionsActionSheet: React.FC<Props> = ({
  availabilityMap,
  fulfillmentDate,
  onClose,
  itemWithSubstitute,
  orderUUID,
  substitutionListOptions,
  variantPacksInOrder,
}) => {
  const substitutionCatalogSearchEnabled =
    useSubstitutionCatalogSearchEnabled();
  const { height } = useWindowDimensions();
  const [quantityProps, setQuantityProps] =
    useState<QuantityPropsStateType | null>(null);

  const searchInitialRoute =
    substitutionCatalogSearchEnabled && substitutionListOptions.length === 0;

  const [quantityBackRoute, setQuantityBackRoute] =
    useState<ChooseSubstitutionRoutes>(
      searchInitialRoute
        ? ChooseSubstitutionRoutes.SearchCatalog
        : ChooseSubstitutionRoutes.GetOptions,
    );

  return (
    <ItemDetailContextProvider>
      <CombinedActionSheet
        contentStyle={styles.modalContent}
        initialRoute={
          searchInitialRoute
            ? ChooseSubstitutionRoutes.SearchCatalog
            : ChooseSubstitutionRoutes.GetOptions
        }
        modalStyle={{
          height: height * 0.85,
        }}
        onClose={() => onClose()}
        renderScene={({ jumpTo, routeKey }) => {
          const goToSelectQuantity = (
            selection: ItemListOption,
            subQuantity: number | undefined,
          ) => {
            setQuantityBackRoute(routeKey);
            setQuantityProps({
              substituteListOption: selection,
              substitutionAvailableQuantity: subQuantity,
            });
            jumpTo(ChooseSubstitutionRoutes.SelectSubstitutionQuantity);
          };
          switch (routeKey) {
            case ChooseSubstitutionRoutes.GetOptions:
              return (
                <ChooseSubstitutionList
                  availabilityMap={availabilityMap}
                  itemListOptions={substitutionListOptions}
                  onSearchPress={() => {
                    jumpTo(ChooseSubstitutionRoutes.SearchCatalog);
                  }}
                  onSelect={goToSelectQuantity}
                />
              );
            case ChooseSubstitutionRoutes.SearchCatalog:
              return (
                <SearchSubstitutionsContent
                  availabilityFulfillmentDate={fulfillmentDate}
                  onSelect={goToSelectQuantity}
                  variantPacksInOrder={variantPacksInOrder}
                />
              );
            case ChooseSubstitutionRoutes.SelectSubstitutionQuantity:
              if (quantityProps) {
                return (
                  <SelectSubstitutionQuantityContent
                    itemWithSubstitute={itemWithSubstitute}
                    onSetSubstitution={(substitutionChoices) => {
                      onClose({
                        substitutionChoices,
                        substitutionVariantPackUUID:
                          itemWithSubstitute.substitution?.original_variant_pack
                            .uuid!,
                      });
                    }}
                    orderUUID={orderUUID}
                    {...quantityProps}
                  />
                );
              }
              return null;
            default:
              return null;
          }
        }}
        routes={{
          [ChooseSubstitutionRoutes.GetOptions]: {
            previousKey: null,
            title: i18n.t('recommended_substitutions'),
          },
          [ChooseSubstitutionRoutes.SearchCatalog]: {
            previousKey: searchInitialRoute
              ? null
              : ChooseSubstitutionRoutes.GetOptions,
          },
          [ChooseSubstitutionRoutes.SelectSubstitutionQuantity]: {
            previousKey: quantityBackRoute,
            title: i18n.t('select_quantity'),
          },
        }}
      />
    </ItemDetailContextProvider>
  );
};

const styles = StyleSheet.create({
  modalContent: {
    flex: 1,
    paddingHorizontal: 8,
  },
});
